import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import SEO, { hgHubSchema, faqSchema, breadcrumbSchema, locationSchema, courseSchema, offerCatalogSchema } from "../../lib/seo";
import { IMG, LOCATIONS, trackConversion, trackPhoneClick } from "../../data/site";
import { PROGRAMS } from "../../data/programs";
import { HG_FAQS } from "../../data/content";
import { Reveal, Eyebrow, SectionShell, FAQBlock } from "../../components/shared";
import { ScheduleFilterable, ReviewCarousel, PrivateLessonForm } from "../../components/interactive";
import { toast } from "sonner";
import { useState, useRef } from "react";
import axios from "axios";
import { getAttribution, newSubmissionId } from "../../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preserve inbound UTM params through to conversion links (attribution requirement)
const useUtmQuery = () => {
  const { search } = useLocation();
  return search || "";
};

// Internal handoff from guymezger.com pages (hgref) or legacy external links still carrying old UTM
const isGMHandoff = (q) => q.includes("hgref=") || q.includes("utm_source=guymezger.com");

// Append/override the trial-form location while preserving the current query (utm/gclid etc.)
const withTrialLocation = (search, slug) => {
  const p = new URLSearchParams(search);
  p.set("location", slug);
  return `?${p.toString()}`;
};

const VALID_TRIAL_LOCATIONS = LOCATIONS.map((l) => l.slug);

// Explicit allowlisted ?location= wins; legacy hgref=location-<slug> next; then the session's
// landing page (a paid North Dallas landing must never silently become Richardson); else Richardson.
const initialTrialLocation = (params) => {
  const explicit = (params.get("location") || "").trim().toLowerCase();
  if (VALID_TRIAL_LOCATIONS.includes(explicit)) return explicit;
  const legacy = ((params.get("hgref") || "").match(/^location-(.+)$/) || [])[1] || "";
  if (VALID_TRIAL_LOCATIONS.includes(legacy)) return legacy;
  const landing = (getAttribution().landing_page || "").toLowerCase();
  if (/\/(locations|near)\/north-dallas|[?&]location=north-dallas/.test(landing)) return "north-dallas";
  return "richardson";
};

const HGButton = ({ to, children, testId, className = "" }) => (
  <Link to={to} data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide px-8 py-4 transition-colors ${className}`}>
    {children} <span aria-hidden="true">→</span>
  </Link>
);

const UtmBadge = () => {
  const q = useUtmQuery();
  if (!isGMHandoff(q)) return null;
  return (
    <p className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white/80 font-mono text-xs px-3 py-1.5 mt-4" data-testid="utm-attribution-badge">
      ● Referred from guymezger.com — this visit is attributed to Guy's site
    </p>
  );
};

export const HGHub = () => {
  const utm = useUtmQuery();
  return (
    <>
      <SEO title="Martial Arts at Hidden Gym — Led by Guy Mezger | Richardson & North Dallas"
        description="Mezger Martial Arts at Hidden Gym: Muay Thai, boxing, BJJ, wrestling, MMA, and kids classes led by UFC veteran Guy Mezger. Book a free trial or join at Hidden Gym."
        schemas={[hgHubSchema(), faqSchema(HG_FAQS), breadcrumbSchema([["Hidden Gym Martial Arts", "/hiddengym/martial-arts"]])]} />
      <section className="relative bg-[#131518] text-white pt-24 pb-20 overflow-hidden grain">
        <img src={IMG.gymInterior} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#131518] to-[#131518]/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow accent="text-neutral-400">Mezger Martial Arts at Hidden Gym</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[0.95]">Train Under<br /><span className="text-neutral-400">Guy Mezger</span><br />at Hidden Gym</h1>
            <p className="mt-5 max-w-2xl text-white/75">Every martial arts class at Hidden Gym is run by Guy Mezger's coaching staff — the same program, the same standard, at both Richardson and North Dallas. Membership includes the full gym.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <HGButton to={`/hiddengym/martial-arts/free-trial${utm}`} testId="hg-hero-trial-btn">Book Your Free Trial</HGButton>
              <Link to={`/hiddengym/membership${utm}`} data-testid="hg-hero-membership-btn" className="inline-flex items-center border border-white/40 text-white font-display font-bold uppercase tracking-wide px-8 py-4 hover:bg-white/10 transition-colors">See Membership</Link>
            </div>
            <UtmBadge />
          </Reveal>
        </div>
      </section>

      <SectionShell className="bg-white">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow accent="text-neutral-400">Who is Guy Mezger?</Eyebrow>
            <h2 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl">A World Champion Runs<br />Our Martial Arts Program.</h2>
            <p className="mt-4 text-[#374151] leading-relaxed">Guy Mezger is a six-time world champion — Ultimate Fighting Champion, three-time King of Pancrase, and World Kickboxing Champion — who then spent 30+ years coaching North Texas. Mezger Martial Arts is Hidden Gym's martial arts department, and Guy is its head instructor.</p>
            <Link to="/about" className="mt-5 inline-block underline font-bold hover:text-neutral-500" data-testid="hg-guy-story-link">Read Guy's full story on guymezger.com →</Link>
          </Reveal>
          <Reveal delay={0.1}><img src={IMG.guy} alt="Guy Mezger, head instructor at Hidden Gym" loading="lazy" className="w-full aspect-[4/3] object-cover object-top border border-black/10" /></Reveal>
        </div>
      </SectionShell>

      <SectionShell>
        <Reveal>
          <Eyebrow accent="text-neutral-400">Programs</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl">Every Discipline. Both Locations.</h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROGRAMS.map((p) => (
            <div key={p.slug} className="bg-white border border-black/10 hover:border-[#0A0B0D] transition-colors flex flex-col" data-testid={`hg-program-card-${p.slug}`}>
              <img src={p.image} alt={p.name} loading="lazy" className={`aspect-[4/3] w-full object-cover ${p.imagePos || "object-center"}`} />
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-display font-black uppercase text-lg leading-tight">{p.name}</h3>
                <p className="font-mono text-[11px] uppercase tracking-wider text-[#5A6270] mt-1 flex-1">{p.level} · Ages {p.ages}</p>
                <div className="mt-3 flex gap-2 text-sm">
                  <Link to={`/programs/${p.slug}`} className="flex-1 text-center border border-black/20 font-bold uppercase py-2 hover:border-black transition-colors" data-testid={`hg-program-${p.slug}-depth-link`}>Learn More</Link>
                  <Link to={`/hiddengym/martial-arts/free-trial${utm}`} className="flex-1 text-center bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-bold uppercase py-2 transition-colors">Trial</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-[#5A6270]">Deep dives on technique, benefits, and FAQs live on <Link to="/" className="underline hover:text-neutral-500">guymezger.com</Link> — signup happens right here at Hidden Gym.</p>
      </SectionShell>

      <SectionShell className="bg-white">
        <Reveal>
          <Eyebrow accent="text-neutral-400">Schedule</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl">Class Schedule</h2>
        </Reveal>
        <div className="mt-8"><ScheduleFilterable /></div>
      </SectionShell>

      <SectionShell>
        <Reveal>
          <Eyebrow accent="text-neutral-400">Reviews</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl">4.9★ Across 580+ Reviews</h2>
        </Reveal>
        <div className="mt-8"><ReviewCarousel /></div>
      </SectionShell>

      <SectionShell className="bg-white">
        <Reveal>
          <Eyebrow accent="text-neutral-400">FAQ</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl">Hidden Gym × Mezger Martial Arts</h2>
        </Reveal>
        <div className="mt-6 max-w-3xl"><FAQBlock faqs={HG_FAQS} testPrefix="hg-faq" /></div>
      </SectionShell>

      <section className="bg-[#131518] text-white py-24 text-center grain relative">
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <Reveal>
            <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">Your First Class<br /><span className="text-neutral-400">Is Free.</span></h2>
            <div className="mt-8 flex justify-center"><HGButton to={`/hiddengym/martial-arts/free-trial${utm}`} testId="hg-final-trial-btn" className="text-lg px-10 py-5">Book Your Free Trial</HGButton></div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export const HGLocation = () => {
  const { slug } = useParams();
  const utm = useUtmQuery();
  const loc = LOCATIONS.find((l) => l.slug === slug);
  if (!loc) return <Navigate to="/hiddengym/martial-arts" replace />;
  return (
    <>
      <SEO title={`Martial Arts at Hidden Gym ${loc.short} | Mezger Martial Arts`}
        description={`Mezger Martial Arts classes at Hidden Gym ${loc.short}: schedule, coaches, and free trial booking. ${loc.address} · ${loc.phone}.`}
        schemas={[hgHubSchema(), locationSchema(loc), breadcrumbSchema([["Hidden Gym Martial Arts", "/hiddengym/martial-arts"], [loc.short, `/hiddengym/martial-arts/${loc.slug}`]])]} />
      <section className="bg-[#131518] text-white pt-24 pb-16 grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow accent="text-neutral-400">Hidden Gym · Martial Arts</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl"><span className="text-neutral-400">{loc.short}</span></h1>
            <p className="mt-3 text-white/70">{loc.address} · <a href={loc.phoneHref} onClick={() => trackPhoneClick(loc.slug, loc.phoneHref)} className="underline" data-testid={`hg-loc-phone-${loc.slug}`}>{loc.phone}</a> · ★ {loc.rating.value.toFixed(1)} ({loc.rating.count} reviews)</p>
            <div className="mt-6"><HGButton to={`/hiddengym/martial-arts/free-trial${withTrialLocation(utm, loc.slug)}`} testId="hg-location-trial-btn">Free Trial at {loc.short}</HGButton></div>
          </Reveal>
        </div>
      </section>
      <SectionShell className="bg-white">
        <div className="grid lg:grid-cols-2 gap-10">
          <iframe title={`Map of ${loc.name}`} src={loc.mapEmbed} className="w-full min-h-[320px] border border-black/10" loading="lazy" allowFullScreen />
          <div>
            <h2 className="font-display font-black uppercase text-2xl">Martial Arts Schedule — {loc.short}</h2>
            <p className="text-sm text-[#5A6270] mt-1 mb-4">Full location details on <Link to={`/locations/${loc.slug}`} className="underline hover:text-neutral-500" data-testid="hg-location-gm-link">guymezger.com/locations/{loc.slug}</Link>.</p>
            <ScheduleFilterable lockLocation={loc.slug} compact />
          </div>
        </div>
      </SectionShell>
    </>
  );
};

export const HGKids = () => {
  const utm = useUtmQuery();
  return (
    <>
      <SEO title="Kids Martial Arts at Hidden Gym | Ages 5–14 | Richardson & North Dallas"
        description="Kids Combat Sports (7+) and Lion Cubs (5–6) at Hidden Gym, coached by Guy Mezger's staff. Discipline, confidence, and safety. Book a free kids trial class."
        schemas={[hgHubSchema(), courseSchema(PROGRAMS.find((p) => p.slug === "kids-martial-arts")), courseSchema(PROGRAMS.find((p) => p.slug === "lion-cubs")), breadcrumbSchema([["Hidden Gym Martial Arts", "/hiddengym/martial-arts"], ["Kids", "/hiddengym/martial-arts/kids"]])]} />
      <section className="relative bg-[#131518] text-white pt-24 pb-20 overflow-hidden grain">
        <img src={IMG.kids} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#131518] to-[#131518]/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow accent="text-neutral-400">Kids Programs · Ages 5–14</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">Confidence.<br />Discipline.<br /><span className="text-neutral-400">Earned.</span></h1>
            <div className="mt-8"><HGButton to={`/hiddengym/martial-arts/free-trial${utm}`} testId="hg-kids-trial-btn">Book a Free Kids Trial</HGButton></div>
          </Reveal>
        </div>
      </section>
      <SectionShell className="bg-white">
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { title: "Kids Combat Sports (7–14)", img: IMG.kids, body: "Real striking and grappling technique in an age-appropriate structure. Kids earn progression through demonstrated skill — which is why the confidence is real. Parents watch every class. Full program details on guymezger.com.", link: "/programs/kids-martial-arts" },
            { title: "Lion Cubs (5–6)", img: IMG.lionCubs, body: "Thirty minutes of structured games building balance, coordination, and listening skills. The on-ramp to the 7+ program, run by coaches who love working with little ones. Currently at Richardson.", link: "/programs/lion-cubs" },
          ].map((k) => (
            <Reveal key={k.title}>
              <div className="border border-black/10 overflow-hidden">
                <img src={k.img} alt={k.title} loading="lazy" className="aspect-video w-full object-cover" />
                <div className="p-6">
                  <h2 className="font-display font-black uppercase text-2xl">{k.title}</h2>
                  <p className="mt-3 text-[#374151] text-sm leading-relaxed">{k.body}</p>
                  <Link to={k.link} className="mt-4 inline-block underline font-bold hover:text-neutral-500" data-testid={`hg-kids-detail-${k.link.split("/").pop()}`}>Program details →</Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10"><ScheduleFilterable compact lockProgram="kids-martial-arts" /></div>
      </SectionShell>
    </>
  );
};

export const HGFreeTrial = () => {
  const utm = useUtmQuery();
  const fromGM = isGMHandoff(utm);
  const params = new URLSearchParams(utm);
  const [form, setForm] = useState({ name: "", phone: "", email: "", location: initialTrialLocation(params), goal: "", program: "", preferred_time: "", sms_consent: false, email_consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const subIdRef = useRef(newSubmissionId());
  const startedRef = useRef(false);
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (!startedRef.current) { startedRef.current = true; trackConversion("form_start", { form: "free_trial" }); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/leads/private-lesson`, {
        ...form,
        kind: "free-trial",
        goal: `FREE TRIAL REQUEST: ${form.goal || "general trial"}`,
        submission_id: subIdRef.current,
        attribution: getAttribution(),
        utm_source: params.get("utm_source") || (params.get("hgref") ? "guymezger.com" : "direct"),
        utm_campaign: params.get("utm_campaign") || params.get("hgref") || "hg-free-trial",
      });
      trackConversion("free_trial_lead", { location: form.location, transaction_id: subIdRef.current, utm_campaign: params.get("utm_campaign") || params.get("hgref") || "hg-free-trial" });
      trackConversion("generate_lead", { lead_type: "free_trial", location: form.location, transaction_id: subIdRef.current, utm_campaign: params.get("utm_campaign") || params.get("hgref") || "hg-free-trial" });
      setDone(true);
      toast.success("Trial booked — see you on the mats!");
    } catch (err) {
      trackConversion("form_error", { form: "free_trial", code: err.response?.status || "network" });
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong — call us and we'll book you by phone.");
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full px-4 py-3 border border-black/20 bg-white text-[#1E2229] focus:outline-none focus:border-[#0A0B0D]";
  return (
    <>
      <SEO title="Free Trial Class | Hidden Gym Martial Arts — Mezger Martial Arts"
        description="Book your free trial martial arts class at Hidden Gym Richardson or North Dallas. No experience needed, gloves and pads provided, no sparring on day one."
        schemas={[hgHubSchema(), { "@context": "https://schema.org", "@type": "Offer", name: "Free Trial Martial Arts Class", price: "0", priceCurrency: "USD", description: "First martial arts class free at Hidden Gym Richardson or North Dallas, coached by Guy Mezger's staff.", offeredBy: { "@type": "Organization", name: "Hidden Gym" } }, breadcrumbSchema([["Hidden Gym Martial Arts", "/hiddengym/martial-arts"], ["Free Trial", "/hiddengym/martial-arts/free-trial"]])]} />
      <section className="bg-[#131518] text-white pt-24 pb-16 grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow accent="text-neutral-400">Free Trial</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">Your First Class<br /><span className="text-neutral-400">Is On Us.</span></h1>
            <p className="mt-4 max-w-xl text-white/70">Pick a location, tell us what interests you, and show up 15 minutes early. Gloves and pads provided. No sparring on day one, ever.</p>
            {fromGM && <p className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white/80 font-mono text-xs px-3 py-1.5 mt-4" data-testid="hg-trial-utm-badge">● Referred by guymezger.com{(params.get("utm_campaign") || params.get("hgref")) ? ` · campaign: ${params.get("utm_campaign") || params.get("hgref")}` : ""}</p>}
          </Reveal>
        </div>
      </section>
      <SectionShell className="bg-white">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div className="max-w-xl">
          {done ? (
            <div className="bg-[#131518] text-white p-8" data-testid="hg-trial-success">
              <h2 className="font-display font-black uppercase text-2xl">You're booked.</h2>
              <p className="mt-2 text-white/70">Hidden Gym staff will confirm your class time by phone or email. Wear athletic clothes, bring water, and arrive 15 minutes early to sign the waiver.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-4 border border-black/10 p-6 md:p-8" data-testid="hg-trial-form">
              <div>
                <label htmlFor="t-name" className="block text-sm font-bold mb-1">Name</label>
                <input id="t-name" required value={form.name} onChange={set("name")} className={input} data-testid="hg-trial-name-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="t-phone" className="block text-sm font-bold mb-1">Phone</label>
                  <input id="t-phone" type="tel" required value={form.phone} onChange={set("phone")} className={input} data-testid="hg-trial-phone-input" />
                </div>
                <div>
                  <label htmlFor="t-email" className="block text-sm font-bold mb-1">Email</label>
                  <input id="t-email" type="email" required value={form.email} onChange={set("email")} className={input} data-testid="hg-trial-email-input" />
                </div>
              </div>
              <div>
                <label htmlFor="t-loc" className="block text-sm font-bold mb-1">Location</label>
                <select id="t-loc" value={form.location} onChange={set("location")} className={input} data-testid="hg-trial-location-select">
                  {LOCATIONS.map((l) => <option key={l.slug} value={l.slug}>{l.short}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="t-program" className="block text-sm font-bold mb-1">Program (optional)</label>
                  <select id="t-program" value={form.program} onChange={set("program")} className={input} data-testid="hg-trial-program-select">
                    <option value="">Not sure yet</option>
                    {PROGRAMS.filter((p) => p.slug !== "private-lessons").map((p) => <option key={p.slug} value={p.slug}>{p.name.split(" (")[0]}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="t-time" className="block text-sm font-bold mb-1">Preferred class / time (optional)</label>
                  <input id="t-time" value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g. Weekday evenings, Sat morning BJJ" className={input} data-testid="hg-trial-time-input" />
                </div>
              </div>
              <div>
                <label htmlFor="t-goal" className="block text-sm font-bold mb-1">What interests you? (optional)</label>
                <input id="t-goal" value={form.goal} onChange={set("goal")} placeholder="e.g. Muay Thai, BJJ, kids class for my 8-year-old…" className={input} data-testid="hg-trial-goal-input" />
              </div>
              <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="hg-trial-honeypot" />
              <div className="space-y-2 text-sm text-[#374151]">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.sms_consent} onChange={set("sms_consent")} className="mt-1" data-testid="hg-trial-sms-consent" />
                  <span>OK to text me about my trial and occasional class updates (SMS)</span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.email_consent} onChange={set("email_consent")} className="mt-1" data-testid="hg-trial-email-consent" />
                  <span>OK to email me schedule news and offers</span>
                </label>
              </div>
              <button type="submit" disabled={busy} data-testid="hg-trial-submit-btn"
                className="bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide py-4 transition-colors disabled:opacity-60">
                {busy ? "Booking…" : "Claim My Free Trial"}
              </button>
            </form>
          )}
        </div>
        <Reveal>
          <img src="/media/photos/coaching-pads.webp" srcSet="/media/photos/coaching-pads-800.webp 800w, /media/photos/coaching-pads.webp 1600w" sizes="(min-width: 1024px) 45vw, 100vw"
            width="1600" height="1067" loading="lazy" alt="Coach working hands-on with two boxing students during a class at Mezger Martial Arts"
            className="w-full aspect-[4/3] object-cover border border-black/10" data-testid="hg-trial-coaching-photo" />
          <p className="mt-3 font-mono text-xs uppercase tracking-wider text-[#5A6270]">Real coaching, every class — no experience needed, no forced sparring.</p>
        </Reveal>
        </div>
      </SectionShell>
    </>
  );
};

export const HGMembership = () => {
  const utm = useUtmQuery();
  const params = new URLSearchParams(utm);
  const fromGM = isGMHandoff(utm);
  const offers = [
    { id: "unlimited", name: "Unlimited Membership", body: "Unlimited Mezger Martial Arts classes plus full Hidden Gym facility access — strength floor, conditioning equipment, open mat. Both locations.", price: "Current rates — inquire at Hidden Gym", popular: true },
    { id: "kids", name: "Kids Membership", body: "Kids Combat Sports (7+) or Lion Cubs (5–6), with coach-tracked progression and viewing areas for parents at every class.", price: "Current rates — inquire at Hidden Gym", popular: false },
    { id: "family", name: "Family Plan", body: "Train together. Discounted rates for households with multiple members. Ask the front desk for current family options.", price: "Ask the front desk about availability", popular: false },
  ];
  return (
    <>
      <SEO title="Membership | Hidden Gym — Includes Mezger Martial Arts Classes"
        description="Hidden Gym membership includes Mezger Martial Arts classes led by Guy Mezger, plus full gym access at Richardson and North Dallas. See current offers."
        schemas={[hgHubSchema(), offerCatalogSchema(), breadcrumbSchema([["Hidden Gym Martial Arts", "/hiddengym/martial-arts"], ["Membership", "/hiddengym/membership"]])]} />
      <section className="bg-[#131518] text-white pt-24 pb-16 grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow accent="text-neutral-400">Membership</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">One Membership.<br /><span className="text-neutral-400">Everything Inside.</span></h1>
            <p className="mt-4 max-w-2xl text-white/70">Standard Hidden Gym membership includes Mezger Martial Arts classes. No separate martial arts fee, no add-ons — the whole building, both locations.</p>
            {fromGM && <p className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white/80 font-mono text-xs px-3 py-1.5 mt-4" data-testid="hg-membership-utm-badge">● Referred by guymezger.com · campaign: {params.get("utm_campaign") || params.get("hgref") || "n/a"}</p>}
          </Reveal>
        </div>
      </section>
      <SectionShell className="bg-white">
        <div className="grid md:grid-cols-3 gap-6">
          {offers.map((o) => (
            <Reveal key={o.id} className="h-full">
              <div className={`h-full flex flex-col border p-7 relative ${o.popular ? "border-white/40 bg-[#131518] text-white" : "border-black/10"}`} data-testid={`hg-membership-card-${o.id}`}>
                {o.popular && <span className="absolute -top-3 left-6 bg-white text-[#0A0B0D] text-xs font-bold uppercase tracking-widest px-3 py-1">Most Popular</span>}
                <h2 className="font-display font-black uppercase text-2xl">{o.name}</h2>
                <p className={`mt-3 text-sm leading-relaxed flex-1 ${o.popular ? "text-white/70" : "text-[#374151]"}`}>{o.body}</p>
                <p className={`mt-4 font-mono text-xs px-2 py-1 inline-block ${o.popular ? "bg-white/10 text-white/70" : "bg-amber-100 text-amber-900"}`}>{o.price}</p>
                <Link to={`/hiddengym/martial-arts/free-trial${utm}`} data-testid={`hg-membership-card-${o.id}-cta`}
                  className={`mt-5 text-center font-display font-bold uppercase tracking-wide py-3 transition-colors ${o.popular ? "bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white" : "border border-[#131518] hover:bg-[#131518] hover:text-white"}`}>
                  Start With a Free Trial
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <img src="/media/photos/mezger-bag-floor.webp" srcSet="/media/photos/mezger-bag-floor-800.webp 800w, /media/photos/mezger-bag-floor.webp 1600w" sizes="(min-width: 1280px) 1200px, 100vw"
            width="1600" height="1067" loading="lazy" alt="Mezger Martial Arts branded heavy bags on the training floor"
            className="w-full aspect-[21/9] object-cover border border-black/10" data-testid="hg-membership-floor-photo" />
          <p className="mt-3 font-mono text-xs uppercase tracking-wider text-[#5A6270]">The Mezger Martial Arts training floor — included in every membership.</p>
        </Reveal>
        <p className="mt-8 text-sm text-[#5A6270] max-w-2xl">Signup and billing are handled by Hidden Gym staff at either front desk or by phone. Martial arts program details, coach bios, and Guy Mezger's story live at <Link to="/" className="underline hover:text-neutral-500" data-testid="hg-membership-gm-link">guymezger.com</Link>.</p>
      </SectionShell>
    </>
  );
};
